import React, { useRef, useState } from 'react'
import { Button, Modal } from '@enamelai/design-system'
import { Icon as UploadIcon } from '@enamelai/design-system/icons/Upload'

const MAX_FILE_SIZE_MB = 4
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

type ImportProceduresProps = {
  show: boolean
  onHide: () => void
  onUpload: (file: File) => void
}

const ImportProcedures: React.FC<ImportProceduresProps> = ({
  show,
  onHide,
  onUpload,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [importedFile, setImportedFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState('')
  const [dragging, setDragging] = useState(false)

  const pickFile = (file?: File): void => {
    if (!file) return
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setImportedFile(null)
      setFileError(`File is larger than ${MAX_FILE_SIZE_MB} MB.`)
      return
    }
    setFileError('')
    setImportedFile(file)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    setDragging(false)
    pickFile(e.dataTransfer.files[0])
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    setDragging(true)
  }

  return (
    <Modal
      open={show}
      onClose={onHide}
      size="sm"
      title="Import file"
      closeOnBackdrop={false}
      className="pc-import-modal"
      secondaryLabel="Cancel"
      onSecondaryClick={onHide}
      primaryLabel="Upload"
      onPrimaryClick={() => {
        if (importedFile) onUpload(importedFile)
        else setFileError('Choose a CSV file to upload.')
      }}
    >
      <div
        className={`pc-dropzone${dragging ? ' pc-dropzone--active' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setDragging(false)}
      >
        <UploadIcon className="pc-dropzone__icon" aria-hidden />
        <div className="pc-dropzone__text">
          <p className="pc-dropzone__title">Drag and drop file here</p>
          <p className="pc-dropzone__hint">
            or choose file to upload ({MAX_FILE_SIZE_MB} MB max size)
          </p>
        </div>
        <Button
          variant="secondary"
          size="md"
          type="button"
          onClick={() => fileInputRef.current?.click()}
        >
          Choose file
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          className="pc-dropzone__input"
          accept=".csv"
          onChange={(e) => {
            pickFile(e.target.files?.[0])
            e.target.value = ''
          }}
        />
        {importedFile && (
          <p className="pc-dropzone__file">{importedFile.name}</p>
        )}
        {fileError && <p className="pc-dropzone__error">{fileError}</p>}
      </div>
    </Modal>
  )
}

export default ImportProcedures
